const BRIDGE_TYPE = 'HBGS_NATIVE'

const BLUETOOTH_TERMINAL_ACTIONS = new Set([
  'BLUETOOTH_VERIFIED',
  'BLUETOOTH_NOT_IN_RANGE',
  'BLUETOOTH_DENIED',
  'BLUETOOTH_PERMISSION_BLOCKED',
  'BLUETOOTH_PERMISSION_REQUIRED',
  'BLUETOOTH_NOT_SUPPORTED',
  'BLUETOOTH_ERROR',
])

const PERMISSIONS_RESULT_ACTION = 'PERMISSIONS_RESULT'
const PERMISSION_RESPONSE_TIMEOUT_MS = 10000
const PERMISSION_REQUEST_TIMEOUT_MS = 30000

const permissionUpdateListeners = new Set()

const isBridgeAvailable = () => {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView?.postMessage
}

const getNumericEnvironmentValue = (value, defaultValue) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue
  }

  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : defaultValue
}

const getOptionalNumericValue = (...values) => {
  const matchedValue = values.find((value) => value !== undefined && value !== null && value !== '')
  const parsedValue = Number(matchedValue)

  return Number.isFinite(parsedValue) ? parsedValue : null
}

const getFirstFilledValue = (...values) => {
  return values.find((value) => value !== undefined && value !== null && value !== '') || null
}

const DEFAULT_BLE_RSSI_THRESHOLD = -70
const DEFAULT_BLE_TIMEOUT_MS = 5000
// RN tarafinda BT acma (~2.5s) + izin akisi scanTimeoutMs'e eklenir; web erken TIMEOUT vermesin.
const BLUETOOTH_NATIVE_PREP_BUFFER_MS = 3500

const buildBluetoothVerificationPayload = (selectedTesis) => {
  // v3.1: Iki ayri UUID (entry + exit). Geriye uyum: ble_beacon_uuid -> entry icin fallback.
  const expectedEntryUuid = getFirstFilledValue(
    selectedTesis?.ble_entry_uuid,
    selectedTesis?.ble_beacon_uuid,
    selectedTesis?.ble_service_uuid
  )
  const expectedExitUuid = getFirstFilledValue(selectedTesis?.ble_exit_uuid)

  return {
    expectedEntryUuid,
    expectedExitUuid,
    // Geriye uyum (eski RN bridge versiyonlari icin tek UUID alani):
    expectedBeaconUuid: expectedEntryUuid,
    expectedServiceUuid: expectedEntryUuid,
    expectedMajor: getOptionalNumericValue(selectedTesis?.ble_major),
    expectedEntryMinor: getOptionalNumericValue(
      selectedTesis?.ble_entry_minor,
      selectedTesis?.ble_minor
    ),
    expectedExitMinor: getOptionalNumericValue(selectedTesis?.ble_exit_minor),
    // Geriye uyum tek minor field:
    expectedMinor: getOptionalNumericValue(
      selectedTesis?.ble_entry_minor,
      selectedTesis?.ble_minor
    ),
    expectedLocalName: selectedTesis?.ble_device_name || null,
    rssiThreshold: getNumericEnvironmentValue(selectedTesis?.ble_rssi_threshold, DEFAULT_BLE_RSSI_THRESHOLD),
    scanTimeoutMs: getNumericEnvironmentValue(selectedTesis?.ble_timeout_ms, DEFAULT_BLE_TIMEOUT_MS),
  }
}

const createBridgeUnavailableResult = () => {
  return {
    ok: false,
    action: 'BLUETOOTH_NOT_SUPPORTED',
    payload: { reason: 'RN_BRIDGE_NOT_AVAILABLE' },
  }
}

const createRequestId = () => {
  return `hbgs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

const safeJsonParse = (value) => {
  if (!value) {
    return null
  }

  if (typeof value === 'object') {
    return value
  }

  if (typeof value !== 'string') {
    return null
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const normalizeIncomingMessage = (rawData) => {
  const parsed = safeJsonParse(rawData)

  if (!parsed || parsed.type !== BRIDGE_TYPE || !parsed.action) {
    return null
  }

  return parsed
}

const postMessageToNative = (message) => {
  window.ReactNativeWebView.postMessage(JSON.stringify(message))
}

const createBridgeMessage = (action, payload = {}, requestId = createRequestId()) => {
  return {
    type: BRIDGE_TYPE,
    action,
    requestId,
    timestamp: Date.now(),
    payload,
  }
}

const normalizePermissionsPayload = (payload = {}) => {
  return {
    bluetooth: payload.bluetooth || 'not_requested',
    geolocation: payload.geolocation || 'not_requested',
  }
}

const notifyPermissionUpdateListeners = (nativePermissions) => {
  permissionUpdateListeners.forEach((listener) => {
    try {
      listener(nativePermissions)
    } catch (error) {
      // Listener hatasi diger abonelikleri bozmamali.
    }
  })
}

const handleIncomingBridgeMessage = (rawData) => {
  const parsed = normalizeIncomingMessage(rawData)

  if (!parsed || parsed.action !== PERMISSIONS_RESULT_ACTION) {
    return
  }

  notifyPermissionUpdateListeners(normalizePermissionsPayload(parsed.payload))
}

const ensureBridgeMessageListeners = () => {
  if (typeof window === 'undefined' || window.__HBGS_BRIDGE_LISTENERS_READY__) {
    return
  }

  window.__HBGS_BRIDGE_LISTENERS_READY__ = true

  window.addEventListener('message', (event) => handleIncomingBridgeMessage(event?.data))
  document.addEventListener('message', (event) => handleIncomingBridgeMessage(event?.data))
  window.addEventListener('HBGS_NATIVE_MESSAGE', (event) => handleIncomingBridgeMessage(event?.detail))

  if (!window.__HBGS_NATIVE_BRIDGE__) {
    window.__HBGS_NATIVE_BRIDGE__ = {}
  }

  window.__HBGS_NATIVE_BRIDGE__.handleNativeMessage = handleIncomingBridgeMessage
}

const subscribePermissionUpdates = (listener) => {
  ensureBridgeMessageListeners()
  permissionUpdateListeners.add(listener)

  return () => {
    permissionUpdateListeners.delete(listener)
  }
}

const waitForBridgeMessage = (requestId, allowedActions, timeoutMs) => {
  return new Promise((resolve) => {
    let isResolved = false

    const complete = (parsed) => {
      if (isResolved) {
        return
      }

      isResolved = true
      clearTimeout(timeoutId)
      window.removeEventListener('message', onWindowMessage)
      document.removeEventListener('message', onDocumentMessage)
      window.removeEventListener('HBGS_NATIVE_MESSAGE', onCustomMessage)
      resolve(parsed)
    }

    const extractAndMatch = (rawData) => {
      const parsed = normalizeIncomingMessage(rawData)

      if (!parsed || parsed.requestId !== requestId || !allowedActions.has(parsed.action)) {
        return
      }

      complete(parsed)
    }

    const onWindowMessage = (event) => extractAndMatch(event?.data)
    const onDocumentMessage = (event) => extractAndMatch(event?.data)
    const onCustomMessage = (event) => extractAndMatch(event?.detail)

    window.addEventListener('message', onWindowMessage)
    document.addEventListener('message', onDocumentMessage)
    window.addEventListener('HBGS_NATIVE_MESSAGE', onCustomMessage)

    const timeoutId = setTimeout(() => {
      complete(null)
    }, timeoutMs)
  })
}

const isFiniteNumber = (value) => {
  return typeof value === 'number' && Number.isFinite(value)
}

const isFilledString = (value) => {
  return typeof value === 'string' && Boolean(value.trim())
}

const isValidMatchedSide = (value) => value === 'entry' || value === 'exit'

const hasValidBluetoothVerifiedPayload = (payload) => {
  return (
    isFiniteNumber(payload?.rssi) &&
    isFilledString(payload?.beaconUuid) &&
    isFiniteNumber(payload?.major) &&
    isFiniteNumber(payload?.minor) &&
    isFiniteNumber(payload?.scanDurationMs)
    // v3.1: matched_side opsiyonel — eski RN bridge'leri yollamayabilir.
    // permissionGate.buildBarrierVerificationPayload undefined ise atlama yapar.
  )
}

const enrichBluetoothPayload = (payload) => {
  // v3.1: matched_side server-side de UUID'den turetir, ama client hint olarak gonderilir.
  const matched = isValidMatchedSide(payload?.matched_side) ? payload.matched_side : undefined
  return matched ? { ...payload, matched_side: matched } : payload
}

const normalizeBluetoothResult = (parsed) => {
  if (parsed.action !== 'BLUETOOTH_VERIFIED') {
    return {
      ok: false,
      action: parsed.action,
      payload: parsed.payload || {},
    }
  }

  if (!hasValidBluetoothVerifiedPayload(parsed.payload)) {
    return {
      ok: false,
      action: 'BLUETOOTH_ERROR',
      payload: { reason: 'INVALID_BLUETOOTH_PAYLOAD' },
    }
  }

  return {
    ok: true,
    action: parsed.action,
    payload: enrichBluetoothPayload(parsed.payload),
  }
}

const waitForBluetoothResult = (requestId, timeoutMs) => {
  return waitForBridgeMessage(requestId, BLUETOOTH_TERMINAL_ACTIONS, timeoutMs).then((parsed) => {
    if (!parsed) {
      return {
        ok: false,
        action: 'BLUETOOTH_ERROR',
        payload: { reason: 'TIMEOUT' },
      }
    }

    return normalizeBluetoothResult(parsed)
  })
}

const requestBluetoothVerification = async (payload) => {
  if (!isBridgeAvailable()) {
    return createBridgeUnavailableResult()
  }

  ensureBridgeMessageListeners()

  const requestId = createRequestId()
  const message = createBridgeMessage('REQUEST_BLUETOOTH_VERIFY', payload, requestId)

  try {
    postMessageToNative(message)
    const scanTimeoutMs = getNumericEnvironmentValue(payload.scanTimeoutMs, DEFAULT_BLE_TIMEOUT_MS)
    return await waitForBluetoothResult(requestId, scanTimeoutMs + BLUETOOTH_NATIVE_PREP_BUFFER_MS)
  } catch (error) {
    return {
      ok: false,
      action: 'BLUETOOTH_ERROR',
      payload: { reason: 'SEND_FAILED' },
    }
  }
}

const checkNativePermissions = async (kinds = ['bluetooth', 'geolocation']) => {
  if (!isBridgeAvailable()) {
    return null
  }

  ensureBridgeMessageListeners()

  const requestId = createRequestId()
  const message = createBridgeMessage(
    'CHECK_PERMISSIONS',
    {
      kinds,
      checkOnly: true,
      mode: 'check',
    },
    requestId
  )

  try {
    postMessageToNative(message)
    const parsed = await waitForBridgeMessage(requestId, new Set([PERMISSIONS_RESULT_ACTION]), PERMISSION_RESPONSE_TIMEOUT_MS)

    if (!parsed) {
      return null
    }

    return normalizePermissionsPayload(parsed.payload)
  } catch (error) {
    return null
  }
}

const requestNativePermissionsWithResult = async (kinds = ['bluetooth', 'geolocation']) => {
  if (!isBridgeAvailable()) {
    return null
  }

  ensureBridgeMessageListeners()

  const requestId = createRequestId()
  const message = createBridgeMessage('REQUEST_PERMISSIONS', { kinds }, requestId)

  try {
    postMessageToNative(message)
    const parsed = await waitForBridgeMessage(requestId, new Set([PERMISSIONS_RESULT_ACTION]), PERMISSION_REQUEST_TIMEOUT_MS)

    if (!parsed) {
      return null
    }

    return normalizePermissionsPayload(parsed.payload)
  } catch (error) {
    return null
  }
}

const requestNativePermissions = (kinds = ['bluetooth', 'geolocation']) => {
  if (!isBridgeAvailable()) {
    return false
  }

  ensureBridgeMessageListeners()
  postMessageToNative(createBridgeMessage('REQUEST_PERMISSIONS', { kinds }))
  return true
}

const closeWebView = () => {
  if (!isBridgeAvailable()) {
    return false
  }

  window.ReactNativeWebView.postMessage('close')
  return true
}

export {
  BRIDGE_TYPE,
  buildBluetoothVerificationPayload,
  checkNativePermissions,
  closeWebView,
  isBridgeAvailable,
  requestBluetoothVerification,
  requestNativePermissions,
  requestNativePermissionsWithResult,
  subscribePermissionUpdates,
  normalizeIncomingMessage,
}
